// Sériation — réordonner lignes et colonnes pour que la structure apparaisse.
//
// Portage de poc/src/seriation.js (parité testée). Classification ascendante en
// lien moyen, puis, à chaque fusion, l'orientation des deux sous-arbres qui
// rapproche le plus les feuilles mises côte à côte. Déterministe.

/**
 * @param n         nombre d'éléments
 * @param distance  (i, j) → distance dans [0, 1], 0 = identiques
 * @returns l'ordre d'affichage, sous forme d'indices
 */
export function ordonner(n: number, distance: (i: number, j: number) => number): number[] {
  if (n <= 2) return Array.from({ length: n }, (_, i) => i);

  const d = Array.from({ length: n }, () => new Float64Array(n));
  const dist = (a: number, b: number) => d[a]?.[b] ?? 0;
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      const v = distance(a, b);
      (d[a] as Float64Array)[b] = v;
      (d[b] as Float64Array)[a] = v;
    }
  }

  let groupes: (number[] | null)[] = Array.from({ length: n }, (_, g) => [g]);

  const lienMoyen = (x: number[], y: number[]) => {
    let somme = 0;
    for (const p of x) for (const q of y) somme += dist(p, q);
    return somme / (x.length * y.length);
  };

  for (let restants = n; restants > 1; restants--) {
    let meilleur: { u: number; w: number; dist: number } | null = null;
    for (let u = 0; u < groupes.length; u++) {
      const gu = groupes[u];
      if (!gu) continue;
      for (let w = u + 1; w < groupes.length; w++) {
        const gw = groupes[w];
        if (!gw) continue;
        const valeur = lienMoyen(gu, gw);
        // À distance égale, le premier rencontré : l'ordre ne dépend pas du hasard.
        if (!meilleur || valeur < meilleur.dist - 1e-12) meilleur = { u, w, dist: valeur };
      }
    }
    if (!meilleur) break;
    const A = groupes[meilleur.u] ?? [];
    const B = groupes[meilleur.w] ?? [];
    const premier = (t: number[]) => t[0] ?? 0;
    const dernier = (t: number[]) => t[t.length - 1] ?? 0;
    const options = [
      { ordre: [...A, ...B], cout: dist(dernier(A), premier(B)) },
      { ordre: [...A, ...[...B].reverse()], cout: dist(dernier(A), dernier(B)) },
      { ordre: [...[...A].reverse(), ...B], cout: dist(premier(A), premier(B)) },
      {
        ordre: [...[...A].reverse(), ...[...B].reverse()],
        cout: dist(premier(A), dernier(B)),
      },
    ];
    let choix = options[0] as (typeof options)[number];
    for (const o of options.slice(1)) if (o.cout < choix.cout - 1e-12) choix = o;
    groupes = groupes.map((g, k) => (k === meilleur.u ? choix.ordre : k === meilleur.w ? null : g));
  }
  return groupes.find((g) => g !== null) ?? [];
}

/** Distance de Jaccard. Deux ensembles vides sont à distance 0. */
export function distanceJaccard<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): number {
  if (!a.size && !b.size) return 0;
  let commun = 0;
  a.forEach((v) => {
    if (b.has(v)) commun++;
  });
  const union = a.size + b.size - commun;
  return union ? 1 - commun / union : 0;
}
